import {
  getCheapestHotelsPriceFromSelection,
  getDestinationId,
  getOriginId,
  getSelectedInboundDate,
  getSelectedOutboundDate,
  getSelectedViewMode,
  isInitialLoad,
  isReturn,
} from '../../selectors';
import getSelectedDaysPrice from '../../selectors/selectedDaysPrice';
import {
  DEPART_MINIEVENT,
  RETURN_MINIEVENT,
  MINI_EVENT,
  ME_PROPERTY_FULFILLED_RATIO_DEPART,
  ME_PROPERTY_FULFILLED_RATIO_RETURN,
  ME_PROPERTY_CURRENT_MONTH,
  ME_PROPERTY_SELECTED_MONTH,
  ME_PROPERTY_DIRECTION,
  ME_PROPERTY_SELECTED_VIEW,
  ME_PROPERTY_SELECTED_PRICE,
  ME_PROPERTY_BUSINESS_VERTICAL,
  ME_PROPERTY_TRIP_TYPE,
  ME_PROPERTY_ORIGIN,
  ME_PROPERTY_DESTINATION,
  ME_PROPERTY_OUTBOUND_DATE,
  ME_PROPERTY_INBOUND_DATE,
  TRIP_TYPE_RETURN,
  TRIP_TYPE_ONE_WAY,
  ME_PROPERTY_INITIAL_LOAD,
  HOTEL_VERTICAL,
} from '../constants/analyticsConstants';
import * as dataTypeConstants from '../constants/dataTypeConstants';
import * as mapping from '../data-parsing/analyticsMappingParser';

import coverageTracker from './coverageTracker';
import priceAccuracyTracker from './priceAccuracyTracker';

import type { MonthChangedAction } from './payload/types';
import type { State } from '../../selectors/types';

let initialOutboundMonth: Date | null = null;
let initialInboundMonth: Date | null = null;
let originalInboundMonth: Date | null = null;
let originalOutboundMonth: Date | null = null;

export const getTripType = (state: State) => {
  const returnTrip = isReturn(state);
  return returnTrip ? TRIP_TYPE_RETURN : TRIP_TYPE_ONE_WAY;
};

export const getFlightResultsLoadedData = (state: State) => {
  const accuracyData = priceAccuracyTracker(
    {},
    state,
    getSelectedDaysPrice(state),
    isInitialLoad(state),
    MINI_EVENT,
  );

  const coverageData = coverageTracker(
    {},
    state,
    initialOutboundMonth,
    initialInboundMonth,
    MINI_EVENT,
  );

  const data = { ...accuracyData };

  data[ME_PROPERTY_FULFILLED_RATIO_DEPART] =
    coverageData[ME_PROPERTY_FULFILLED_RATIO_DEPART];
  data[ME_PROPERTY_FULFILLED_RATIO_RETURN] =
    coverageData[ME_PROPERTY_FULFILLED_RATIO_RETURN];
  data[ME_PROPERTY_TRIP_TYPE] = getTripType(state);

  return data;
};

export const getHotelResultsLoadedData = (state: State) => ({
  [ME_PROPERTY_INITIAL_LOAD]: isInitialLoad(state),
});

export const getMonthChangedData = (
  state: State,
  action: MonthChangedAction,
) => {
  originalInboundMonth = getSelectedInboundDate(state);
  originalOutboundMonth = getSelectedOutboundDate(state);

  if (!initialOutboundMonth) initialOutboundMonth = originalOutboundMonth;
  if (!initialInboundMonth) initialInboundMonth = originalInboundMonth;
  if (action.payload.source === dataTypeConstants.AUTOMATED_SOURCE) return null;

  let originalMonth = null;
  let originalYear = null;
  const direction = mapping.minieventDirectionHelper(action.payload.direction);

  if (direction === RETURN_MINIEVENT) originalMonth = originalInboundMonth;
  if (direction === DEPART_MINIEVENT) originalMonth = originalOutboundMonth;

  if (originalMonth !== null) {
    originalYear = originalMonth.getFullYear();
    originalMonth = originalMonth.getMonth() + 1;
    originalMonth = `00${originalMonth}`.slice(-2);
  }

  const { month } = action.payload;
  const monthStr = `00${month}`.slice(-2);

  const monthChangedData = {
    [ME_PROPERTY_CURRENT_MONTH]: `${originalMonth}-${originalYear}`,
    [ME_PROPERTY_SELECTED_MONTH]: `${monthStr}-${action.payload.year}`,
    [ME_PROPERTY_DIRECTION]: direction,
    [ME_PROPERTY_SELECTED_VIEW]: mapping.minieventViewModeHelper(
      getSelectedViewMode(state),
    ),
  };

  return monthChangedData;
};

const buildLocation = (id: string) => ({
  location_attribute_encoding: 'SS_GEO1_ID',
  location_id: id,
});

const getCommonsDate = (date: Date) => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
});

const buildDates = (outboundDate: Date, inboundDate: Date | null) => ({
  [ME_PROPERTY_OUTBOUND_DATE]: getCommonsDate(outboundDate),
  ...(inboundDate
    ? { [ME_PROPERTY_INBOUND_DATE]: getCommonsDate(inboundDate) }
    : {}),
});

export const getResultSelectedData = (state: State, vertical: string) => ({
  [ME_PROPERTY_SELECTED_VIEW]: mapping.minieventViewModeHelper(
    getSelectedViewMode(state),
  ),
  [ME_PROPERTY_SELECTED_PRICE]:
    vertical === HOTEL_VERTICAL
      ? getCheapestHotelsPriceFromSelection(
          state,
          getSelectedOutboundDate(state),
          getSelectedInboundDate(state),
        )
      : getSelectedDaysPrice(state) || 0,
  [ME_PROPERTY_BUSINESS_VERTICAL]: vertical,
  [ME_PROPERTY_ORIGIN]: buildLocation(getOriginId(state)),
  [ME_PROPERTY_DESTINATION]: buildLocation(getDestinationId(state)),
  ...buildDates(getSelectedOutboundDate(state), getSelectedInboundDate(state)),
});
